/**
 * 🧑‍💻 SWE-2: Profile 페이지 - 사용량 위젯 연동
 * 🧑‍💻 SWE-5: 구독 정보 연동
 * 🧑‍💻 SWE-6: 회원 탈퇴 기능
 * 🎨 BILLING-UX: 결제 정보 표시 강화
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUsage, formatStorage, formatNumber } from '../hooks/useUsage';
import { useLicense, TIER_INFO } from '../hooks/useLicense';
import { usePayment } from '../hooks/usePayment';
import DeleteAccountModal from '../components/DeleteAccountModal';

export default function Profile() {
  const { user } = useAuth();
  const { data: usage, period, isLoading: usageLoading, error: usageError, refetch } = useUsage();
  const { currentLicense, isLoading: licenseLoading, tierLabel } = useLicense();
  const { subscription, isLoading: paymentLoading, loadSubscription, isSubscriptionCanceled } = usePayment();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // 데이터 로드 (한 번만)
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // 남은 일수 계산
  const daysRemaining = useMemo(() => {
    if (!currentLicense?.valid_until) return null;
    const endDate = new Date(currentLicense.valid_until);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [currentLicense?.valid_until]);

  // 구독 상태 텍스트
  const getSubscriptionStatusText = () => {
    if (!currentLicense) return { text: '미구독', color: 'text-gray-500' };
    if (isSubscriptionCanceled) return { text: '취소 예정', color: 'text-yellow-500' };
    if (currentLicense.status === 'active') return { text: '활성', color: 'text-green-500' };
    if (currentLicense.status === 'expired') return { text: '만료됨', color: 'text-red-500' };
    return { text: currentLicense.status, color: 'text-gray-500' };
  };

  return (
    <div className="py-12">
      <div className="section-container">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">내 정보</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="card text-center">
              <div className="w-24 h-24 bg-gray-100 dark:bg-zinc-800 rounded-full mx-auto mb-4 overflow-hidden">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.display_name || '프로필'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl text-gray-500 dark:text-gray-400">
                    👤
                  </div>
                )}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                {user?.display_name || '사용자'}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">{user?.email}</p>
              
              <button className="mt-4 btn-secondary w-full">
                프로필 수정
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Account Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">계정 정보</h3>
              <dl className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border-gray">
                  <dt className="text-gray-700 dark:text-gray-300">이메일</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">{user?.email}</dd>
                </div>
                <div className="flex justify-between py-2 border-b border-border-gray">
                  <dt className="text-gray-700 dark:text-gray-300">이메일 인증</dt>
                  <dd>
                    {user?.email_verified ? (
                      <span className="text-green-600">✓ 인증됨</span>
                    ) : (
                      <span className="text-orange-500">미인증</span>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-gray-700 dark:text-gray-300">가입일</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {user?.created_at 
                      ? new Date(user.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })
                      : '-'}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Subscription Info - Enhanced */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">구독 정보</h3>
                <Link
                  to="/billing"
                  className="text-sm text-space-blue hover:underline"
                >
                  결제 관리 →
                </Link>
              </div>
              
              {licenseLoading || paymentLoading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 dark:bg-zinc-700 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-zinc-700 rounded w-1/3"></div>
                </div>
              ) : (
                <>
                  {/* 구독 상태 헤더 */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-zinc-800/50 rounded-lg mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        currentLicense?.tier === 'team' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        currentLicense?.tier === 'pro' ? 'bg-space-blue/10 dark:bg-space-blue/20' :
                        'bg-gray-100 dark:bg-zinc-700'
                      }`}>
                        <span className="text-2xl">
                          {currentLicense?.tier === 'team' ? '👥' : currentLicense?.tier === 'pro' ? '⭐' : '🆓'}
                        </span>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {tierLabel}
                        </p>
                        <p className={`text-sm ${getSubscriptionStatusText().color}`}>
                          ● {getSubscriptionStatusText().text}
                        </p>
                      </div>
                    </div>
                    {currentLicense?.tier !== 'free' && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {TIER_INFO[currentLicense?.tier as keyof typeof TIER_INFO]?.priceLabel || '-'}
                        </p>
                        <p className="text-xs text-gray-500">/ 월</p>
                      </div>
                    )}
                  </div>
                  
                  <dl className="space-y-4">
                    {/* 등록 기기 */}
                    <div className="flex justify-between py-2 border-b border-border-gray dark:border-zinc-700">
                      <dt className="text-gray-700 dark:text-gray-300">등록 기기</dt>
                      <dd className="text-gray-900 dark:text-white font-medium">
                        {currentLicense ? `${currentLicense.devices_used} / ${currentLicense.max_devices}대` : '-'}
                      </dd>
                    </div>
                    
                    {/* 구독 시작일 */}
                    {currentLicense?.tier !== 'free' && subscription?.current_period_start && (
                      <div className="flex justify-between py-2 border-b border-border-gray dark:border-zinc-700">
                        <dt className="text-gray-700 dark:text-gray-300">구독 시작일</dt>
                        <dd className="text-gray-900 dark:text-white">
                          {new Date(subscription.current_period_start).toLocaleDateString('ko-KR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </dd>
                      </div>
                    )}
                    
                    {/* 다음 결제일 / 만료 예정일 */}
                    <div className="flex justify-between py-2 border-b border-border-gray dark:border-zinc-700">
                      <dt className="text-gray-700 dark:text-gray-300">
                        {isSubscriptionCanceled ? '만료 예정일' : '다음 결제일'}
                      </dt>
                      <dd className={`font-medium ${
                        isSubscriptionCanceled 
                          ? 'text-yellow-600 dark:text-yellow-400' 
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {currentLicense?.valid_until 
                          ? new Date(currentLicense.valid_until).toLocaleDateString('ko-KR', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })
                          : '-'}
                        {daysRemaining !== null && daysRemaining <= 7 && (
                          <span className="ml-2 text-xs text-orange-500">
                            ({daysRemaining}일 남음)
                          </span>
                        )}
                      </dd>
                    </div>
                    
                    {/* 다음 결제 금액 */}
                    {currentLicense?.tier !== 'free' && !isSubscriptionCanceled && (
                      <div className="flex justify-between py-2 border-b border-border-gray dark:border-zinc-700">
                        <dt className="text-gray-700 dark:text-gray-300">다음 결제 금액</dt>
                        <dd className="text-gray-900 dark:text-white font-medium">
                          {TIER_INFO[currentLicense?.tier as keyof typeof TIER_INFO]?.priceLabel || '-'}
                        </dd>
                      </div>
                    )}
                    
                    {/* 결제 관리 */}
                    <div className="flex justify-between py-2">
                      <dt className="text-gray-700 dark:text-gray-300">결제 관리</dt>
                      <dd className="text-gray-900 dark:text-white">
                        <Link to="/billing" className="text-space-blue hover:underline text-sm">
                          결제 관리 →
                        </Link>
                      </dd>
                    </div>
                  </dl>
                  
                  {/* 취소 예정 경고 */}
                  {isSubscriptionCanceled && (
                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-start gap-2">
                        <span>⚠️</span>
                        <div>
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            구독 취소가 예약되어 있습니다
                          </p>
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                            만료일까지 현재 플랜의 모든 기능을 이용할 수 있습니다.
                          </p>
                          <Link 
                            to="/billing" 
                            className="inline-block mt-2 text-xs text-yellow-700 dark:text-yellow-300 hover:underline font-medium"
                          >
                            구독 유지하기 →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Upgrade CTA for Free users */}
              {(!currentLicense || currentLicense.tier === 'free') && (
                <div className="mt-6 p-4 bg-gradient-to-r from-space-blue/10 to-purple-500/10 dark:from-space-blue/20 dark:to-purple-500/20 rounded-lg border border-space-blue/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Pro로 업그레이드하세요
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        더 많은 기기와 모든 기능을 사용할 수 있습니다
                      </p>
                    </div>
                    <Link 
                      to="/pricing"
                      className="px-4 py-2 bg-space-blue text-white text-sm font-medium rounded-lg hover:bg-space-blue/90 transition-colors"
                    >
                      플랜 보기
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Usage Widget - 🧑‍💻 SWE-2: 실제 데이터 연동 */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">사용량</h3>
                {period && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(period.start).toLocaleDateString('ko-KR', { month: 'long' })} 기준
                  </span>
                )}
              </div>
              
              {usageLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 dark:bg-zinc-700 rounded w-1/3 mb-2"></div>
                      <div className="h-2 bg-gray-200 dark:bg-zinc-700 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : usageError ? (
                <div className="text-center py-4">
                  <p className="text-red-500 text-sm mb-2">{usageError}</p>
                  <button 
                    onClick={refetch}
                    className="text-space-blue hover:underline text-sm"
                  >
                    다시 시도
                  </button>
                </div>
              ) : usage ? (
                <div className="space-y-6">
                  {/* 토큰 사용량 */}
                  <UsageBar
                    label="토큰 사용량"
                    used={usage.tokens.used}
                    limit={usage.tokens.limit}
                    percentage={usage.tokens.percentage}
                    formatValue={formatNumber}
                    icon="🔤"
                  />
                  
                  {/* 요청 수 */}
                  <UsageBar
                    label="API 요청"
                    used={usage.requests.used}
                    limit={usage.requests.limit}
                    percentage={usage.requests.percentage}
                    formatValue={formatNumber}
                    icon="🔗"
                  />
                  
                  {/* 저장 공간 */}
                  <UsageBar
                    label="저장 공간"
                    used={usage.storage.used}
                    limit={usage.storage.limit}
                    percentage={usage.storage.percentage}
                    formatValue={formatStorage}
                    icon="💾"
                  />
                </div>
              ) : null}
              
              {/* 업그레이드 안내 */}
              {usage && (usage.tokens.percentage >= 80 || usage.requests.percentage >= 80) && (
                <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        사용량이 한도에 가까워지고 있습니다
                      </p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                        Pro 플랜으로 업그레이드하면 더 많은 리소스를 사용할 수 있습니다.
                      </p>
                      <a 
                        href="/pricing" 
                        className="inline-block mt-2 text-sm text-yellow-700 dark:text-yellow-300 hover:underline font-medium"
                      >
                        플랜 비교하기 →
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Danger Zone */}
            <div className="card border-red-200 dark:border-red-800">
              <h3 className="text-lg font-semibold text-red-600 mb-4">계정 삭제</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
              </p>
              <button 
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                계정 삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 회원 탈퇴 모달 */}
      <DeleteAccountModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
      />
    </div>
  );
}

/**
 * 사용량 바 컴포넌트
 */
interface UsageBarProps {
  label: string;
  used: number;
  limit: number;
  percentage: number;
  formatValue: (value: number) => string;
  icon: string;
}

function UsageBar({ label, used, limit, percentage, formatValue, icon }: UsageBarProps) {
  // 색상 결정 (퍼센트에 따라)
  const getBarColor = (pct: number) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-yellow-500';
    return 'bg-space-blue';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <span>{icon}</span>
          {label}
        </span>
        <span className="text-gray-900 dark:text-white font-medium text-sm">
          {formatValue(used)} / {formatValue(limit)}
        </span>
      </div>
      <div className="w-full bg-gray-100 dark:bg-zinc-800 rounded-full h-2.5 overflow-hidden">
        <div 
          className={`h-2.5 rounded-full transition-all duration-500 ${getBarColor(percentage)}`} 
          style={{ width: `${Math.min(100, percentage)}%` }}
        ></div>
      </div>
      <div className="text-right mt-0.5">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {percentage.toFixed(1)}% 사용
        </span>
      </div>
    </div>
  );
}
